import { Styled } from "./styles";
import logo from "@/assets/aider.png";
import { NavLink, useNavigate } from "react-router-dom";
import { Box, List, Skeleton, Typography, useTheme } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import FolderIcon from "@mui/icons-material/Folder";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HelpIcon from "@mui/icons-material/Help";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { i18n } from "@/utils/i18n";
import storage from "@/utils/storage";
import useFormStatus from "@/context/form";
import useAlert from "@/components/common/modals/alert/context";
import useQueryOrganization from "@/hooks/queries/useQueryOrganization";
import React from "react";
import useUser from "@/context/user";

const ListItem = Styled.ListItem.withComponent(NavLink);

const openHelpCenter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.open("https://intercom.help/aider/sv", "_blank", "noopener,noreferrer");
};

const Sidebar: React.FC = () => {
    const theme = useTheme();

    const oid = storage.getItem("oid");
    const navigate = useNavigate();

    const authenticated = useUser(state => state.authenticated);
    const { data: organization, isLoading } = useQueryOrganization();

    const isDirty = useFormStatus(state => state.isDirty);
    const open = useAlert(state => state.open);

    const onNavigate = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, route: string) => {
        if (isDirty) {
            e.preventDefault();

            return open({
                title: i18n.t("alert.form.title"),
                message: i18n.t("alert.form.message"),
                actions: { confirm: { callback: () => navigate(route) } },
            });
        }

        navigate(route);
    };

    return (
        <Styled.Drawer variant="persistent" anchor="left" open={true}>
            <Styled.Logo src={logo} alt="Aider Logo" />

            {isLoading || !authenticated ? (
                <Box display="flex" alignItems="center" gap={1} margin="10px 0px">
                    <Skeleton
                        sx={{ backgroundColor: theme.palette.grey[500] }}
                        variant="circular"
                        width={32}
                        height={32}
                    />
                    <Skeleton
                        sx={{ backgroundColor: theme.palette.grey[500] }}
                        variant="text"
                        width={140}
                        height={28}
                    />
                </Box>
            ) : (
                organization && (
                    <Box display="flex" alignItems="center" gap={1} margin="10px 0px">
                        <ApartmentIcon />
                        <Typography color="white" variant="h5">
                            {organization.name}
                        </Typography>
                    </Box>
                )
            )}

            <List>
                {isLoading || !authenticated ? (
                    <React.Fragment>
                        {[0, 1, 2].map(index => (
                            <Styled.ListItemSkeleton key={index}>
                                <Skeleton
                                    sx={{ backgroundColor: theme.palette.grey[500] }}
                                    variant="circular"
                                    width={24}
                                    height={24}
                                />
                                <Skeleton
                                    sx={{ backgroundColor: theme.palette.grey[500] }}
                                    variant="text"
                                    width="60%"
                                    height={24}
                                />
                            </Styled.ListItemSkeleton>
                        ))}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <ListItem
                            to={`/organizations/${oid}/reports`}
                            onClick={e => onNavigate(e, `/organizations/${oid}/reports`)}
                            className={({ isActive }) => (isActive ? "active" : "")}
                        >
                            <FolderIcon style={{ marginRight: "10px" }} />
                            <Typography variant="body2" sx={{ color: "#ffff" }}>
                                {i18n.t("navigation.reports")}
                            </Typography>
                        </ListItem>
                        <ListItem
                            to={`/organizations/${oid}/audited-reports`}
                            onClick={e => onNavigate(e, `/organizations/${oid}/audited-reports`)}
                            className={({ isActive }) => (isActive ? "active" : "")}
                        >
                            <TaskAltIcon style={{ marginRight: "10px" }} />
                            <Typography variant="body2" sx={{ color: "#ffff" }}>
                                {i18n.t("navigation.auditedReports")}
                            </Typography>
                        </ListItem>
                        <ListItem
                            to={`/organizations/${oid}/settings`}
                            onClick={e => onNavigate(e, `/organizations/${oid}/settings`)}
                            className={({ isActive }) => (isActive ? "active" : "")}
                        >
                            <SettingsIcon style={{ marginRight: "10px" }} />
                            <Typography variant="body2" sx={{ color: "#ffff" }}>
                                {i18n.t("navigation.organizationSettings")}
                            </Typography>
                        </ListItem>
                        <ListItem to="/#" onClick={openHelpCenter}>
                            <HelpIcon style={{ marginRight: "10px" }} />
                            <Typography variant="body2" sx={{ color: "#ffff" }}>
                                {i18n.t("navigation.helpCenter")}
                            </Typography>
                        </ListItem>
                    </React.Fragment>
                )}
            </List>
        </Styled.Drawer>
    );
};

export default Sidebar;
